/**
 * Slider
 * Using Swiper.js
 *
 * @see https://idangero.us/swiper/api
 * @author Daniel Kvistgaard <dkvistgaard@vertic.com>
 */

import {
    Swiper,
    Navigation,
    Autoplay,
    EffectFade,
    Pagination,
    Scrollbar,
} from "swiper/dist/js/swiper.esm";
import React from "react";
import ReactDOM from "react-dom";

import TitlePagination from "./components/title-pagination/title-pagination";
import NumericalPagination from "./components/numerical-pagination/numerical-pagination";
import TimelinePagination from "./components/timeline-pagination/timeline-pagination";

Swiper.use([Autoplay, Navigation, EffectFade, Pagination, Scrollbar]);

class Slider {
    constructor(container, options) {
        this.container = container;
        this.options = options;
        this.swiperOptions = { ...Swiper.defaults, ...this.options };
        this.windowWidth = window.innerWidth;

        const slides = this.container.querySelectorAll(
            `.${this.swiperOptions.slideClass}:not(.${this.swiperOptions.slideDuplicateClass})`
        );

        if (slides.length <= this.swiperOptions.slidesPerView) return false;

        if (options.pagination) {
            this.renderPagination();
        }

        this.swiper = new Swiper(container, {
            ...this.options,
        });

        if (this.options.autoplay) {
            window.addEventListener("focus", this.onWindowFocus);
        }

        return this.swiper;
    }

    destroy() {
        if (this.options.autoplay) {
            window.removeEventListener("focus", this.onWindowFocus);
        }
    }

    onWindowFocus = () => {
        if (this.windowWidth !== window.innerWidth) {
            this.swiper.update();

            this.windowWidth = window.innerWidth;
        }
    };

    renderPagination() {
        let className = "swiper-pagination";

        if (this.options.pagination.type === "title-pagination") {
            className = "slider-title-pager";

            // Setting up required options for the title-pagination
            this.options.loop = true;
            this.options.pagination.type = "custom";
            this.options.pagination.modifierClass = `${className}--`;
            this.options.pagination.renderCustom = this.renderTitlePagination;
            if (this.options.autoplay)
                this.options.autoplay.disableOnInteraction = false;
        }

        if (this.options.pagination.type === "numerical-pagination") {
            className = "slider-numerical-pager";

            // Setting up required options for the numerical-pagination
            this.options.loop = true;
            this.options.pagination.type = "custom";
            this.options.pagination.modifierClass = `${className}--`;
            this.options.pagination.renderCustom =
                this.renderNumericalPagination;
            if (this.options.autoplay)
                this.options.autoplay.disableOnInteraction = false;
        }

        if (this.options.pagination.type === "timeline-pagination") {
            className = "slider-timeline-pager";

            // Setting up required options for the timeline-pagination
            this.options.loop = false;
            this.options.pagination.type = "custom";
            this.options.pagination.modifierClass = `${className}--`;
            this.options.pagination.renderCustom =
                this.renderTimelinePagination;
            if (this.options.autoplay)
                this.options.autoplay.disableOnInteraction = false;
        }

        // Check if the pagination element does exist but isn't defined
        if (!this.options.pagination.el) {
            const paginationEl = this.container.querySelector(`.${className}`);
            if (paginationEl) {
                this.options.pagination.el = paginationEl;
            }
        }

        // Creating pagination element in case it doesn't exist
        if (!this.options.pagination.el) {
            const pagination = document.createElement("div");
            pagination.classList.add(className);

            this.container.appendChild(pagination);
            this.options.pagination.el = `.${className}`;
        }

        // Updating swiperOptions
        this.swiperOptions = { ...Swiper.defaults, ...this.options };
    }

    renderTitlePagination = (swiper) => {
        const { el } = swiper.params.pagination;
        const paginationEl =
            el instanceof HTMLElement
                ? el
                : this.container.querySelector(swiper.params.pagination.el);
        const slides = this.container.querySelectorAll(
            `.${swiper.params.slideClass}:not(.${swiper.params.slideDuplicateClass})`
        );

        const paginations = Array.from(slides).map((slide, index) => {
            const { paginationTitle: title } = slide.dataset;

            return {
                id: Array.from(slide.parentNode.children).indexOf(slide),
                active: index === swiper.realIndex,
                title,
                onClick: swiper.slideTo.bind(swiper),
            };
        });

        const titlePagination = (
            <TitlePagination
                paginations={paginations}
                progressDelay={this.swiper ? swiper.params.speed : 0}
                progressSpeed={swiper.params.autoplay.delay}
            />
        );

        ReactDOM.render(titlePagination, paginationEl);
    };

    renderNumericalPagination = (swiper, current, total) => {
        const { el } = swiper.params.pagination;
        const paginationEl =
            el instanceof HTMLElement
                ? el
                : this.container.querySelector(swiper.params.pagination.el);

        const paginationData = {
            current,
            total,
        };

        const numericalPagination = (
            <NumericalPagination
                paginationData={paginationData}
                progressDelay={this.swiper ? swiper.params.speed : 0}
                progressSpeed={swiper.params.autoplay.delay}
            />
        );

        ReactDOM.render(numericalPagination, paginationEl);
    };

    renderTimelinePagination = (swiper) => {
        const { el } = swiper.params.pagination;
        const paginationEl =
            el instanceof HTMLElement
                ? el
                : this.container.querySelector(swiper.params.pagination.el);
        const slides = this.container.querySelectorAll(
            `.${swiper.params.slideClass}:not(.${swiper.params.slideDuplicateClass})`
        );

        const paginations = Array.from(slides).map((slide, index) => {
            const { paginationYear: year } = slide.dataset;

            return {
                id: index,
                active: index === swiper.realIndex,
                year,
                onClick: swiper.slideTo.bind(swiper),
            };
        });

        const titlePagination = (
            <TimelinePagination
                paginations={paginations}
                swiperIndex={swiper.realIndex}
                speed={swiper.params.speed}
            />
        );

        ReactDOM.render(titlePagination, paginationEl);
    };
}

export default Slider;
